import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth lives in an httpOnly cookie set by the backend — never in localStorage.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const response = await axios.post(`${API}/auth/login`, payload);
    if (response.data?.requires_2fa) {
      return response.data; // caller must re-submit with the TOTP code
    }
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      /* the cookie is cleared server-side; local state reset below */
    }
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/users/me`, data);
    setUser(response.data);
    return response.data;
  };

  const becomeOrganizer = async () => {
    const response = await axios.post(`${API}/users/me/become-organizer`, {});
    setUser(response.data);
    return response.data;
  };

  const isFavorite = (eventId) => Array.isArray(user?.favorites) && user.favorites.includes(eventId);

  const toggleFavorite = async (eventId) => {
    if (!user) throw new Error('unauth');
    const currentlyFav = isFavorite(eventId);
    // Optimistic update
    setUser((prev) => prev ? {
      ...prev,
      favorites: currentlyFav
        ? (prev.favorites || []).filter((id) => id !== eventId)
        : [...(prev.favorites || []), eventId],
    } : prev);
    try {
      const url = `${API}/users/me/favorites/${eventId}`;
      const response = currentlyFav
        ? await axios.delete(url)
        : await axios.post(url, {});
      setUser(response.data);
      return !currentlyFav;
    } catch (err) {
      // Rollback on error
      setUser((prev) => prev ? {
        ...prev,
        favorites: currentlyFav
          ? [...(prev.favorites || []), eventId]
          : (prev.favorites || []).filter((id) => id !== eventId),
      } : prev);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, checkAuth, setUser, becomeOrganizer, isFavorite, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
};
